import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { URLPaths } from 'models/enums';
import FolderNamingDialog from 'domains/core/components/folders/FolderNamingDialog';
import SegmentsPageLayout from './SegmentsPageLayout';
import useSegmentsWithFolders from 'hooks/queries/useSegmentsWithFolders';
import useSegmentQueryManager from 'hooks/useSegmentQueryManager';
import useCreateSegmentFolder from 'hooks/mutations/useCreateSegmentFolder';
import { isRootFolder } from 'utils/folders';
import useArchivedSegmentsList from 'hooks/queries/useArchivedSegmentsList';

const SegmentsPageWithFolders = () => {
    const [isCreateFolderModalVisible, setIsCreateFolderModalVisible] = useState(false);
    const [isArchivedView, setIsArchivedView] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const history = useHistory();
    const location = useLocation();
    const { folderId, changeViewParams } = useSegmentQueryManager();

    // Only call the appropriate query based on current view
    const segmentsWithFoldersQuery = useSegmentsWithFolders({
        folderId,
        searchTerm,
        reactQueryOptions: { enabled: !isArchivedView },
    });
    const archivedSegmentsQuery = useArchivedSegmentsList({
        searchTerm,
        reactQueryOptions: { enabled: isArchivedView },
    });

    // Check if we're on the archive URL and set state accordingly
    useEffect(() => {
        const isArchiveUrl = location.pathname === URLPaths.SEGMENTS_ARCHIVED;
        setIsArchivedView(isArchiveUrl);
    }, [location.pathname]);

    const { refetch: refetchContent } = segmentsWithFoldersQuery;

    const isUserOnRootLevel = isRootFolder(folderId);

    const createFolder = (folderName: string) => {
        const body = {
            name: folderName,
        };
        saveFolder(body);
    };

    const onCreateFolderSuccess = (response: { id: number }) => {
        changeViewParams({ newFolderId: response.id });
        refetchContent();
        setIsCreateFolderModalVisible(false);
    };

    const { mutate: saveFolder, isLoading: isFolderSaving } = useCreateSegmentFolder({
        onSuccess: onCreateFolderSuccess,
    });

    const handleToggleArchivedView = () => {
        const newArchivedView = !isArchivedView;
        setIsArchivedView(newArchivedView);

        // Update URL based on archived view state
        if (newArchivedView) {
            history.push(URLPaths.SEGMENTS_ARCHIVED);
        } else {
            // Use changeViewParams to properly handle folder paths
            changeViewParams({ newFolderId: folderId });
        }
    };

    const currentSegmentsQuery = isArchivedView ? archivedSegmentsQuery : segmentsWithFoldersQuery;

    const createButtonDropdownOptions = [
        { label: 'Audience', onClick: () => history.push(URLPaths.SEGMENT_CREATE) },
        { label: 'Folder', onClick: () => setIsCreateFolderModalVisible(true) },
    ];

    return (
        <>
            <FolderNamingDialog
                disableActionButton={isFolderSaving}
                initialValue="Untitled Folder"
                isDialogVisible={isCreateFolderModalVisible}
                onClose={() => setIsCreateFolderModalVisible(false)}
                onSubmit={createFolder}
                title="create new folder"
            />
            <SegmentsPageLayout
                createButtonDropdownOptions={createButtonDropdownOptions}
                isUserOnRootLevel={isUserOnRootLevel}
                segmentsQuery={currentSegmentsQuery}
                isArchivedView={isArchivedView}
                onToggleArchivedView={handleToggleArchivedView}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
            />
        </>
    );
};

export default SegmentsPageWithFolders;
